import { Box, Modal, Typography } from "@mui/material";
import { useRef } from "react";
import useColors from "../../../../../api/hooks/useColors";
import IconButton from "../../../../../components/buttons/IconButton";
import IconContainer from "../../../../../components/IconContainer";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { CONTROLS_WITH_REVOKE_TOKEN_BUTTON } from "../../../../../utils/constants";
import AutoRemediationAvailableChip from "../../../../remediation/plan/components/AutoRemediationAvailableChip";
import { ReactComponent as CloseIcon } from "../../../environment/icons/close_icon_currentColor.svg";
import { ReactComponent as PDFIcon } from "../../../environment/icons/Export_PDF_currentColor.svg";
import { logos } from "../../../environment/icons/iconExport";
import {
  IntegrationCategory,
  IntegrationsMapping,
} from "../../IntegrationsMapping";
import CVESection from "./CVESection";
import InventorySection from "./InventorySection";
import NotificationSection from "./NotificationSection";
import { handlePDFExport } from "./pdfUtils";
import SecuritySection from "./SecuritySection";
import TicketingSection from "./TicketingSection";

export const POTENTIAL_HEADER_CLASSNAME = "potential-header";
export const POTENTIAL_DESCRIPTION_CLASSNAME = "potential-description";
export const HEADER_BUTTONS_CLASSNAME = "header-buttons";

export default function PotentialModal({
  showModal,
  setShowModal,
  integrationName,
  integrationsInfoQuery,
}) {
  const colors = useColors();
  const containerRef = useRef(null);

  const integrationInfo = integrationsInfoQuery.data?.[integrationName];
  const details = IntegrationsMapping[integrationName];
  const integrationLogo = details?.newLogo ? details?.newLogo : logos?.logo;
  const potentialDescription =
    ![
      IntegrationCategory.TICKETING,
      IntegrationCategory.ALERTING,
      IntegrationCategory.AUTOMATION,
      IntegrationCategory.SIEM,
    ].includes(details.category) &&
    `${details.displayName} will be analyzed for security control misconfigurations, coverage, and security events.`;

  return (
    <>
      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <Box
          ref={containerRef}
          width="980px"
          maxHeight="90vh"
          padding="32px 16px"
          position="absolute"
          borderRadius="10px"
          top="50%"
          left="50%"
          sx={{
            transform: "translate(-50%, -50%)",
            backgroundColor: colors.modal_bg,
            outline: "none",
            boxShadow: "0px 1px 4px 0px " + colors.grey_24percent,
          }}
          display="flex"
          flexDirection="column"
          gap="24px"
        >
          {integrationsInfoQuery.isLoading ? (
            <LoadingIcon size={24} />
          ) : (
            <>
              <div
                className={POTENTIAL_HEADER_CLASSNAME}
                style={{
                  width: "100%",
                  height: "32px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0 16px",
                }}
              >
                <Box display="flex" gap={2} alignItems="center">
                  <IconContainer IconSVG={integrationLogo} size="M" />
                  <Typography variant="subtitle1">
                    The Breeze integration with {details.displayName}
                  </Typography>
                  {integrationsInfoQuery?.data?.[integrationName]?.controls &&
                    CONTROLS_WITH_REVOKE_TOKEN_BUTTON.some((control_id) =>
                      integrationsInfoQuery.data[integrationName].controls.some(
                        (control) => control.control_id === control_id
                      )
                    ) && (
                      <AutoRemediationAvailableChip text="Auto-remediation" />
                    )}
                </Box>
                <div
                  className={HEADER_BUTTONS_CLASSNAME}
                  style={{ display: "flex", gap: "16px", alignItems: "center" }}
                >
                  <Box display="flex" gap={1}>
                    <IconButton
                      onClick={() =>
                        handlePDFExport(
                          containerRef.current,
                          integrationName,
                          integrationInfo,
                          colors
                        )
                      }
                      IconSVG={PDFIcon}
                      size="S"
                    />
                    {/* TODO - Show the email button after we implement the email feature */}
                    {/* <IconButton IconSVG={EmailIcon} size="S" disabled={true} /> */}
                  </Box>
                  <Box
                    sx={{ cursor: "pointer" }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="32px"
                    width="32px"
                  >
                    <IconButton
                      IconSVG={CloseIcon}
                      buttonStyle="standard"
                      size="S"
                      onClick={() => setShowModal(false)}
                    />
                  </Box>
                </div>
              </div>

              <div
                className={POTENTIAL_DESCRIPTION_CLASSNAME}
                style={{
                  padding: "0 16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <Typography variant="body1" color={colors.text_primary}>
                  {potentialDescription ||
                    IntegrationsMapping[integrationName].description}
                </Typography>
                {integrationName === IntegrationsMapping.wiz.codeName && (
                  <Typography variant="body1" color={colors.text_primary}>
                    Note: Currently supports AWS.
                  </Typography>
                )}
              </div>
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                overflow="auto"
                padding="16px"
              >
                {integrationInfo?.controls && (
                  <SecuritySection integrationInfo={integrationInfo} />
                )}
                {integrationInfo?.collectors && (
                  <InventorySection integrationInfo={integrationInfo} />
                )}
                {details.hasVulnerabilityCollection && (
                  <CVESection displayName={details.displayName} />
                )}
                {details.category === IntegrationCategory.TICKETING && (
                  <TicketingSection displayName={details.displayName} />
                )}
                {[
                  IntegrationCategory.ALERTING,
                  IntegrationCategory.SIEM,
                  IntegrationCategory.AUTOMATION,
                ].includes(details.category) && (
                  <NotificationSection displayName={details.displayName} />
                )}
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
}
